import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CommonWrapper from '../../components/ui_components/CommonComponentsStructureCss';
import colors from '../../styles/colors';
import { apiGetUserProfile } from '../../api/userCardApi';
import type { UserProfile, UserProfileOwnedCard } from '../../api/userCardApi';
import CardDetailModal from './CardDetailModal';
import type { Card } from '../../types/models/card';

const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';

const toImgSrc = (path: string | null | undefined) =>
  path ? (path.startsWith('http') ? path : storageUrl + path) : null;

const RARITY_COLOR: Record<string, string> = {
  common: 'rgba(255,255,255,0.55)',
  rare: '#4a9eff',
  epic: '#c27ff5',
  legendary: 'gold',
};

// ─── Styled ────────────────────────────────────────────────────────────────────

const PageInner = styled.div`
  padding: 40px 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 40px;
`;

const Username = styled.h1`
  font-size: 28px;
  font-weight: 900;
  color: #fff;
  margin: 0 0 4px;
`;

const Nickname = styled.p`
  font-size: 15px;
  color: rgba(255, 255, 255, 0.45);
  margin: 0;
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 16px;
`;

// ─── Stats ─────────────────────────────────────────────────────────────────────

const StatsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 40px;
`;

const StatChip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 12px 20px;
`;

const StatLabel = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatValue = styled.span`
  font-size: 22px;
  font-weight: 900;
  color: #fff;
`;

const StatValueGold = styled(StatValue)`
  color: gold;
`;

// ─── Wrestlers ─────────────────────────────────────────────────────────────────

const WrestlersSection = styled.div`
  margin-bottom: 40px;
`;

const WrestlersList = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const WrestlerCard = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px 16px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
  color: #fff;

  &:hover {
    border-color: rgba(255, 215, 0, 0.5);
    background-color: rgba(255, 215, 0, 0.06);
  }
`;

const WrestlerAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.08);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
  }
`;

const WrestlerName = styled.span`
  font-size: 14px;
  font-weight: 600;
`;

// ─── Cards grid ────────────────────────────────────────────────────────────────

const CardsSection = styled.div``;

const CardsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 7px 12px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background-color: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 14px;
  width: 220px;
  &:focus { outline: none; border-color: gold; }
  &::placeholder { color: rgba(255,255,255,0.3); }
`;

const ExpansionSelect = styled.select`
  padding: 7px 10px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background-color: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  &:focus { outline: none; border-color: gold; }
  option { background-color: #1a1a1a; }
`;

const CardsCount = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.35);
  margin-left: auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
`;

const CardItem = styled.div`
  cursor: pointer;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background-color: #222;
  transition: transform 0.12s, border-color 0.12s;

  &:hover {
    transform: scale(1.03);
    border-color: rgba(255, 215, 0, 0.5);
  }
`;

const CardImgWrapper = styled.div`
  width: 100%;
  aspect-ratio: 2 / 3;
  background-color: #2a2a2a;
  overflow: hidden;
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardImgPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.15);
  font-size: 11px;
`;

const CardFooter = styled.div`
  padding: 5px 7px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #1a1a1a;
`;

const CardIdLabel = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
`;

const RarityDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const QtyBadge = styled.span`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  padding: 1px 4px;
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 60px;
  font-size: 15px;
`;

// ─── Component ─────────────────────────────────────────────────────────────────

const UserProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<UserProfileOwnedCard | null>(null);
  const [search, setSearch] = useState('');
  const [expansionFilter, setExpansionFilter] = useState<number | 'all'>('all');

  useEffect(() => {
    if (!username) return;
    setIsLoading(true);
    setNotFound(false);
    apiGetUserProfile(username)
      .then((data) => setProfile(data))
      .catch(() => setNotFound(true))
      .finally(() => setIsLoading(false));
  }, [username]);

  const validCards = useMemo(
    () => (profile?.owned_cards ?? []).filter((e) => e.card !== null),
    [profile]
  );

  const expansionOptions = useMemo(() => {
    const map = new Map<number, string>();
    validCards.forEach((e) => e.card!.expansions.forEach((exp) => map.set(exp.id, exp.name)));
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [validCards]);

  const filtered = useMemo(() => {
    return validCards.filter((entry) => {
      const card = entry.card!;
      if (expansionFilter !== 'all' && !card.expansions.some((e) => e.id === expansionFilter)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return card.wrestlers.some((w) => w.name.toLowerCase().includes(q));
      }
      return true;
    });
  }, [validCards, expansionFilter, search]);

  if (isLoading) return <CommonWrapper><StatusText>Caricamento...</StatusText></CommonWrapper>;
  if (notFound || !profile) return <CommonWrapper><StatusText>Utente non trovato.</StatusText></CommonWrapper>;

  // Costruisce un oggetto Card dal profilo per passarlo al modal
  const toCard = (entry: UserProfileOwnedCard): Card | null => {
    if (!entry.card) return null;
    return {
      id: entry.card.id,
      rarity: entry.card.rarity,
      fronte_path: entry.card.fronte_path,
      retro_path: null,
      notes: null,
      author_user_id: entry.card.author_user_id,
      author_name: entry.card.author_name,
      author_user: entry.card.author_user,
      wrestlers: entry.card.wrestlers.map((w) => ({ ...w, avatar_path: w.avatar_path ?? '' })),
      ppvs: [],
      belts: [],
      tags: [],
      expansions: entry.card.expansions.map((e) => ({ ...e, expansion_path: null, pivot: { drop_rate: 0 } })),
      price: null,
      shop_disabled: false,
      reference_url: entry.card.reference_url,
      reference_string: entry.card.reference_string,
      reference_data: entry.card.reference_data ?? null,
    };
  };

  const selectedCard = selectedEntry ? toCard(selectedEntry) : null;

  return (
    <CommonWrapper>
      <PageInner>

        {/* Header */}
        <Header>
          <Username>{profile.username}</Username>
          {profile.nickname_forum && <Nickname>{profile.nickname_forum}</Nickname>}
        </Header>

        {/* Stats */}
        <StatsRow>
          <StatChip>
            <StatLabel>Carte uniche</StatLabel>
            <StatValue>{profile.stats.unique_cards}</StatValue>
          </StatChip>
          <StatChip>
            <StatLabel>Copie totali</StatLabel>
            <StatValue>{profile.stats.total_copies}</StatValue>
          </StatChip>
          <StatChip>
            <StatLabel>Legendary</StatLabel>
            <StatValueGold>{profile.stats.legendary_count}</StatValueGold>
          </StatChip>
        </StatsRow>

        {/* Wrestlers assegnati */}
        {profile.wrestlers.length > 0 && (
          <WrestlersSection>
            <SectionTitle>Wrestler assegnati</SectionTitle>
            <WrestlersList>
              {profile.wrestlers.map((w) => {
                const avatarSrc = toImgSrc(w.avatar_path);
                return (
                  <WrestlerCard key={w.id} onClick={() => navigate(`/wrestlers/i_${w.id}_d_${w.name}`)}>
                    <WrestlerAvatar>
                      {avatarSrc && <img src={avatarSrc} alt={w.name} />}
                    </WrestlerAvatar>
                    <WrestlerName>{w.name}</WrestlerName>
                  </WrestlerCard>
                );
              })}
            </WrestlersList>
          </WrestlersSection>
        )}

        {/* Carte possedute */}
        <CardsSection>
          <CardsHeader>
            <SectionTitle style={{ margin: 0 }}>Carte possedute</SectionTitle>
            <CardsCount>
              {(() => {
                const catalogTotal = expansionFilter !== 'all'
                  ? (profile.stats.catalog_per_expansion[String(expansionFilter)] ?? '?')
                  : profile.stats.total_catalog_cards;
                return `${filtered.length} / ${catalogTotal} carte`;
              })()}
            </CardsCount>
          </CardsHeader>

          <FiltersRow>
            <SearchInput
              type="text"
              placeholder="Cerca per wrestler..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {expansionOptions.length > 0 && (
              <ExpansionSelect
                value={expansionFilter}
                onChange={(e) => setExpansionFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              >
                <option value="all">Tutte le espansioni</option>
                {expansionOptions.map((exp) => (
                  <option key={exp.id} value={exp.id}>{exp.name}</option>
                ))}
              </ExpansionSelect>
            )}
          </FiltersRow>

          {validCards.length === 0 && (
            <StatusText style={{ marginTop: 24 }}>Nessuna carta ancora.</StatusText>
          )}

          {validCards.length > 0 && filtered.length === 0 && (
            <StatusText style={{ marginTop: 24 }}>Nessuna carta corrisponde ai filtri.</StatusText>
          )}

          <Grid>
            {filtered.map((entry) => {
              const card = entry.card!;
              const src = toImgSrc(card.fronte_path);
              return (
                <CardItem key={entry.card_id} onClick={() => setSelectedEntry(entry)}>
                  <CardImgWrapper>
                    {src
                      ? <CardImg src={src} alt={`Carta #${card.id}`} />
                      : <CardImgPlaceholder>#{card.id}</CardImgPlaceholder>
                    }
                  </CardImgWrapper>
                  <CardFooter>
                    <CardIdLabel>#{card.id}</CardIdLabel>
                    {entry.quantity > 1 && <QtyBadge>x{entry.quantity}</QtyBadge>}
                    <RarityDot $color={RARITY_COLOR[card.rarity] ?? 'rgba(255,255,255,0.3)'} />
                  </CardFooter>
                </CardItem>
              );
            })}
          </Grid>
        </CardsSection>

      </PageInner>

      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          ownedQuantity={selectedEntry?.quantity ?? 0}
          isAuthenticated={false}
          onClose={() => setSelectedEntry(null)}
        />
      )}
    </CommonWrapper>
  );
};

export default UserProfilePage;
